'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ============================================================================
// Types & Schema
// ============================================================================

const paypalFormSchema = z.object({
  sandboxed: z.boolean(),
  clientId: z.string().min(1, 'PayPal client id is required'),
  clientSecret: z.string().min(1, 'PayPal client secret is required'),
  enableExpressCheckout: z.boolean(),
})

type FormValues = z.infer<typeof paypalFormSchema>

interface PaypalFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function PaypalForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: PaypalFormProps) {
  const { t } = useTranslation('integrations')

  const form = useForm<FormValues>({
    resolver: zodResolver(paypalFormSchema),
    defaultValues: {
      sandboxed: defaultValues.credentials?.sandboxed ?? false,
      clientId: defaultValues.credentials?.clientId ?? '',
      clientSecret: defaultValues.credentials?.clientSecret ?? '',
      // Opt-in, unlike Apple/Google Pay: flipping this re-places PayPal on
      // every published page, so existing stores must ask for it.
      enableExpressCheckout: defaultValues.metadata?.enableExpressCheckout ?? false,
    },
  })

  const { register, handleSubmit, formState: { errors }, watch, setValue } = form

  const handleFormSubmit = async (data: FormValues) => {
    const { enableExpressCheckout, ...credentials } = data
    await onSubmit({
      credentials,
      // Merge over existing metadata — it carries paypalWebhookId.
      metadata: { ...defaultValues.metadata, enableExpressCheckout },
    })
  }

  const sandboxed = watch('sandboxed')

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Environment Warning */}
      {sandboxed && (
        <Alert variant="default" className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-900">{t('paypal.sandboxModeActive')}</AlertTitle>
          <AlertDescription className="text-amber-700">
            {t('paypal.sandboxModeWarning')}
          </AlertDescription>
        </Alert>
      )}

      {/* Environment Toggle */}
      <Card>
        <CardHeader>
          <CardTitle>{t('paypal.environmentTitle')}</CardTitle>
          <CardDescription>
            {t('paypal.environmentDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sandboxed" className="font-medium">
                {t('paypal.useSandbox')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('paypal.sandboxHint')}
              </p>
            </div>
            <Switch
              id="sandboxed"
              checked={sandboxed}
              onCheckedChange={(checked) => setValue('sandboxed', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* API Credentials */}
      <Card>
        <CardHeader>
          <CardTitle>{t('paypal.apiCredentials')}</CardTitle>
          <CardDescription>
            {t('paypal.apiCredentialsDescription', { mode: sandboxed ? t('forms.common.sandbox') : t('forms.common.live') })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="paypalClientId">
              {t('paypal.clientId')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="paypalClientId"
              placeholder={t('paypal.clientIdPlaceholder')}
              disabled={isLoading}
              {...register('clientId')}
            />
            {errors.clientId && (
              <p className="text-xs text-destructive">{errors.clientId.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="paypalClientSecret">
              {t('paypal.clientSecret')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="paypalClientSecret"
              type="password"
              placeholder={t('paypal.clientSecretPlaceholder')}
              disabled={isLoading}
              {...register('clientSecret')}
            />
            {errors.clientSecret && (
              <p className="text-xs text-destructive">{errors.clientSecret.message}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Express Checkout */}
      <Card>
        <CardHeader>
          <CardTitle>{t('paypal.expressCheckoutTitle')}</CardTitle>
          <CardDescription>
            {t('paypal.expressCheckoutDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enableExpressCheckout" className="font-medium">
                {t('paypal.enableExpressCheckout')}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t('paypal.expressCheckoutHint')}
              </p>
            </div>
            <Switch
              id="enableExpressCheckout"
              checked={watch('enableExpressCheckout')}
              onCheckedChange={(checked) => setValue('enableExpressCheckout', checked)}
              disabled={isLoading}
            />
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('paypal.setupInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-muted p-4">
            <ol className="list-inside list-decimal space-y-2 text-sm text-muted-foreground">
              <li>
                {t('paypal.setupStep1')}{' '}
                <a
                  href="https://developer.paypal.com/dashboard/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  {t('paypal.setupStep1Link')}
                </a>
              </li>
              <li dangerouslySetInnerHTML={{ __html: t('paypal.setupStep2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('paypal.setupStep3', { mode: sandboxed ? t('forms.common.sandbox') : t('forms.common.live') }) }} />
              <li>{t('paypal.setupStep4')}</li>
              <li dangerouslySetInnerHTML={{ __html: t('paypal.setupStep5') }} />
            </ol>
          </div>
        </CardContent>
      </Card>
    </form>
  )
}
